import mevOfflineQueue from './mevOfflineQueue';
import { mevStorage } from '../mev/utils/mevStorage';
import connectionMonitor from './connectionMonitor';
import mevOrderJobManager from '../mev/utils/mevOrderJobManager';
import { getCloudMevStoreStatus, isPlaceholderIdapprl, loadSavedCloudMevCertRecord, pickIssuedIdapprl, postPreparedCloudMevSale } from '../cloud-mev/api/cloudMevApi';
import { getPrivateKey } from './certificates/getPrivateKey';
import { readThumbprintPointer } from './certificates/thumbprintPointer';
import { readStoredCloudMevEnv } from '../cloud-mev/cloudMevEnv';
import { caseForEssaiHeader } from '../cloud-mev/essaiTestCase';
import { logExceptionScenarios } from '../mev/utils/exceptionScenario';
import { isOnlineFiscalOrder, isOrderPaid } from '../mev/utils/onlineFiscal';
import { logAPIResponse } from './backgroundLogger';
import { toast } from 'sonner';
import { localization } from '../localization';
import { offlineBatchRequest, offlineBillSizeLabel, offlineRequestBytes, oldestRowsWithinLimit, readOfflineBatchLimitBytes } from './offlineBatchLimit';

const ONLINE_ORDER_DEBUG = 'online order debug';

function onlineDebugAction(event: string, order: any): string {
  const printMode = String(order?.modImpr || '').toUpperCase();
  if (event === 'cancel_after_print') return isOrderPaid(order) ? 'cancel' : 'unpaid cancel';
  if (event === 'refund') return 'refund';
  if (event === 'correct_payment_step1' || event === 'correct_payment_step2') return 'payment change';
  if (event === 'reprint' || printMode === 'RPR') return 'reprint';
  if (event === 'final') return 'closing';
  return event;
}

function logOnlineOrderDebug(order: any, event: string, payload: any, returned: any) {
  if (!isOnlineFiscalOrder(order)) return;
  console.log(ONLINE_ORDER_DEBUG, onlineDebugAction(event, order), {
    payload,
    return: returned,
  });
}

// Accepted bills are logged by handleCloudMevResponse when they print; rejected ones never reach it.
function logRejectedResponse(result: any, reqTrans: any) {
  logAPIResponse(
    result?.rqUrl || 'WEB-SRM transaction',
    'POST',
    result?.rqRequest || { reqTrans },
    result?.rqResponse || {},
    0,
    result?.rqStatus || 400,
  ).catch((error) => {
    console.error('[MEV] Failed to write rejected API response:', error);
  });
}

const MEV_ACTIONS = new Set([
  'temporary',
  'kitchen',
  'final',
  'cancel_before_print',
  'cancel_after_print',
  'refund',
  'correct_payment_step1',
  'correct_payment_step2',
  'reprint',
]);

function logKitchenMev(headers: unknown, payload: unknown, returned: unknown) {
  console.log('[Kitchen MEV] headers\n' + JSON.stringify(headers ?? null, null, 2));
  console.log('[Kitchen MEV] payload\n' + JSON.stringify(payload ?? null, null, 2));
  console.log('[Kitchen MEV] return\n' + JSON.stringify(returned ?? null, null, 2));
}

function plainOrder(order: any) {
  try {
    return JSON.parse(JSON.stringify(order, (_key, value) => {
      if (value && typeof value === 'object' && typeof value.toDate === 'function') {
        return value.toDate().toISOString();
      }
      if (value && typeof value === 'object' && value._methodName) return null;
      return value;
    }));
  } catch {
    return { ...(order || {}) };
  }
}

function queueRow(transaction: any) {
  const payload = transaction.payload || {};
  return {
    queueId: transaction.id,
    orderId: payload.orderId || transaction.metadata?.orderId,
    event: payload.event || 'final',
    order: payload.order || {},
    createdAt: payload.createdAt || transaction.metadata?.createdAt || transaction.createdAt,
    reqTrans: payload.reqTrans || payload.order?.reqTrans || null,
  };
}

function signedReqTrans(formatted: any) {
  const reqTrans = formatted?.reqTrans;
  if (!reqTrans?.transActu?.signa?.actu) return null;
  return JSON.parse(JSON.stringify(reqTrans));
}

function restoreChain(reqTrans: any) {
  const preced = reqTrans?.transActu?.signa?.preced;
  if (preced) mevStorage.setPreviousSignature(preced);
}

function isReproductionBill(reqTrans: any, order?: any) {
  return reqTrans?.transActu?.modImpr === 'RPR' || order?.modImpr === 'RPR';
}

function errorLists(result: any) {
  return [
    result?.rqResponse?.retourTrans?.retourTransLot?.listErr,
    result?.rqResponse?.retourTrans?.retourTransActu?.listErr,
    result?.rqResponse?.retourTrans?.listErr,
    result?.rqResponse?.listErr,
  ].filter((list) => Array.isArray(list) && list.length);
}

function rqErrorText(result: any) {
  return errorLists(result).flat().map((err: any) => {
    const where = err?.noTrans ? `${err.noTrans}: ` : '';
    return `${where}${err?.mess || err?.message || err?.codRetour || ''}`.trim();
  }).filter(Boolean).join('; ');
}

function failedTransactionNumbers(result: any) {
  const numbers = new Set<string>();
  let unnamed = false;
  errorLists(result).flat().forEach((err: any) => {
    const noTrans = String(err?.noTrans || err?.id || '').trim();
    if (noTrans) numbers.add(noTrans);
    else unnamed = true;
  });
  return { numbers, unnamed };
}

function readStoredHeader(envirn?: string) {
  try {
    const storeId = mevStorage.getStoreId() || '';
    const env = String(envirn || mevStorage.getEnvirn() || 'DEV').toLowerCase();
    const saved = (storeId && localStorage.getItem(`transHeader_${storeId}_${env}`))
      || localStorage.getItem(`transHeader_${env}`)
      || '';
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function certificateDeviceId(storeId: string, envirn: string): string {
  const saved = loadSavedCloudMevCertRecord(storeId, envirn);
  const direct = pickIssuedIdapprl(saved.idApprl, saved.cert?.idapprl);
  if (!isPlaceholderIdapprl(direct)) return direct;
  if (typeof localStorage === "undefined") return "";
  const env = String(envirn || "DEV").toUpperCase();
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = String(localStorage.key(index) || "");
    if (!key.includes("cloudMev") || !key.toLowerCase().includes("cert")) continue;
    try {
      const parsed = JSON.parse(localStorage.getItem(key) || "");
      if (!parsed || typeof parsed !== "object") continue;
      if (parsed.envirn && String(parsed.envirn).toUpperCase() !== env) continue;
      const id = pickIssuedIdapprl(parsed.idApprl, parsed.cert?.idapprl, parsed.IDAPPRL);
      if (!isPlaceholderIdapprl(id)) return id;
    } catch {
      // Another stored certificate record may hold the issued device id.
    }
  }
  return "";
}

function issuedDeviceId(storeId: string, envirn: string, status: any) {
  return pickIssuedIdapprl(
    certificateDeviceId(storeId, envirn),
    status?.idApprl,
    status?.snapshot?.idApprl,
    status?.snapshot?.cert?.idapprl,
  );
}

async function windowsDeviceId(envirn: string): Promise<string> {
  const invoke = typeof window !== "undefined" ? (window as any).api?.invoke : null;
  if (!invoke) return "";
  try {
    const storeResult = await invoke("list-mev-certificates");
    const certs = storeResult?.certificates || [];
    const env = String(envirn || "DEV").toUpperCase();
    const names = certs.map((cert: any) => String(cert?.FriendlyName || ""));
    const patterns = [
      new RegExp(`^MEV_Terminal_(.+)_${env}$`),
      new RegExp(`^MEV_Key_(.+)_${env}$`),
      new RegExp(`^CloudMEV_${env}_(.+)$`),
    ];
    for (const pattern of patterns) {
      for (const name of names) {
        const id = pattern.exec(name)?.[1] || "";
        if (!isPlaceholderIdapprl(id)) return id;
      }
    }
  } catch {
    return "";
  }
  return "";
}

async function frozenTransactionHeader(storeId: string, envirn: string) {
  const status = await getCloudMevStoreStatus(storeId, envirn).catch(() => null);
  const local = readStoredHeader(envirn);
  const frozen = status?.snapshot?.transHeader;
  const base = (frozen?.authCode ? frozen : null) || (local?.authCode ? local : null);
  if (!base) return null;
  const deviceId = pickIssuedIdapprl(
    issuedDeviceId(storeId, envirn, status),
    await windowsDeviceId(envirn),
    base.IDAPPRL,
  );
  if (isPlaceholderIdapprl(deviceId)) return base;
  const header = { ...base, IDAPPRL: deviceId };
  try {
    localStorage.setItem(`transHeader_${storeId}_${String(envirn).toLowerCase()}`, JSON.stringify(header));
  } catch {
    // The sale can still use the header for this request.
  }
  return header;
}

async function transmissionHeaders(sampleBill: any, storeId: string, envirn: string, actus?: string[]) {
  const started = Date.now();
  const transHeader = await frozenTransactionHeader(storeId, envirn);
  if (!transHeader) throw new Error('Transaction header information is required');
  const authCode = String(transHeader.authCode || '');
  const IDAPPRL = String(transHeader.IDAPPRL || '');
  console.log('[MEV timing] headers', { ms: Date.now() - started, idApprl: IDAPPRL });
  if (!authCode || !IDAPPRL) throw new Error('Transaction header information is required');
  const privateKey = await getPrivateKey(storeId, envirn.toLowerCase());
  if (!privateKey) throw new Error('Private key not found');
  const signatures = (actus && actus.length ? actus : [sampleBill?.signa?.actu]).map((value) => String(value || '')).filter(Boolean);
  if (!signatures.length) throw new Error('Missing bill signature for the transmission signature');
  const signedText = `${authCode}${IDAPPRL}${signatures.join('')}`;
  console.log('Back to station', 'transmission signature input', {
    authCodeLength: authCode.length,
    idApprl: IDAPPRL,
    signatureCount: signatures.length,
    actuLength: signatures[0].length,
    signedLength: signedText.length,
  });
  const signResult = await (window as any).api.signData(signedText, privateKey);
  if (!signResult?.success || !signResult.signature) throw new Error('Transmission signature failed');
  const signature = String(signResult.signature);
  const SIGNATRANSM = signature.length >= 88 ? signature.slice(0, 88) : signature.padEnd(88, '=');
  const casEssai = caseForEssaiHeader(envirn, storeId);
  console.log("[CASESSAI] sale", { storeId, envirn, casEssai });
  return {
    CASESSAI: casEssai,
    NOTPS: String(transHeader.noTPS || ''),
    NOTVQ: String(transHeader.noTVQ || ''),
    IDAPPRL,
    APPRLINIT: String(transHeader.APPRLINIT || ''),
    IDSEV: String(transHeader.IDSEV || ''),
    IDVERSI: String(transHeader.IDVERSI || ''),
    CODCERTIF: String(transHeader.CODCERTIF || ''),
    IDPARTN: String(transHeader.IDPARTN || ''),
    VERSI: String(transHeader.VERSI || ''),
    VERSIPARN: String(transHeader.VERSIPARN || ''),
    SIGNATRANSM,
    EMPRCERTIFTRANSM: String(sampleBill?.emprCertifSEV || readThumbprintPointer(storeId, envirn) || ''),
  };
}

export async function prepareStationBill(order: any, event: string) {
  const formatted = await mevOrderJobManager.formatAndPrintBill(order, event, false, false);
  const reqTrans = signedReqTrans(formatted);
  if (reqTrans && formatted?.qrCodeUrl) reqTrans.qrCodeUrl = formatted.qrCodeUrl;
  return reqTrans;
}

export async function queueOfflineBill(args: {
  orderId: string;
  event: string;
  order: any;
  storeId: string;
}) {
  const createdAt = Date.now();
  const order = plainOrder({ ...args.order, id: args.orderId });
  const formatted = await mevOrderJobManager.formatAndPrintBill(order, args.event, true);
  const datTrans = String(formatted?.reqTrans?.transActu?.datTrans || createdAt);
  try {
    await mevOfflineQueue.addTransaction(
      {
        orderId: args.orderId,
        event: args.event,
        order,
        storeId: args.storeId,
        createdAt,
        reqTrans: formatted?.reqTrans?.transActu?.signa?.actu
          ? JSON.parse(JSON.stringify(formatted.reqTrans))
          : { transActu: { noTrans: order.orderNumber || '', datTrans } },
      },
      {
        orderId: args.orderId,
        sessionId: order.sessionId || '',
        transactionNumber: String(order.orderNumber || order.counter || ''),
        datTrans,
        createdAt,
        storeId: args.storeId,
        userId: order.posUserId || '',
      }
    );
    mevOfflineQueue.enterOfflineMode();
    if (args.event === 'kitchen') {
      logKitchenMev(null, formatted?.reqTrans || null, {
        offline: true,
        queued: true,
        noTrans: formatted?.reqTrans?.transActu?.noTrans || null,
      });
    }
    logExceptionScenarios(args.event, 'queue', formatted?.reqTrans?.transActu, args.order);
    console.log('internet disconnected debug', 'saved to IndexedDB', {
      orderId: args.orderId,
      event: args.event,
      storeId: args.storeId,
      datTrans,
      reqTrans: formatted?.reqTrans || null,
    });
  } catch (error) {
    console.error('internet disconnected debug', 'IndexedDB save failed', error);
  }
  return formatted;
}

export async function flushOfflineBills(current?: {
  orderId: string;
  event: string;
  order: any;
  storeId: string;
}) {
  if (!connectionMonitor.getStatus()) {
    throw new Error('Still offline. The bills stay queued until the network is back.');
  }
  const pending = await mevOfflineQueue.getUnsentTransactions(current?.storeId);
  if (!pending.length && !current) return null;

  const storeId = current?.storeId || pending[0]?.metadata?.storeId || pending[0]?.payload?.storeId || '';
  if (!storeId) throw new Error('Missing store id for offline batch');
  const envirn = readStoredCloudMevEnv(storeId) || 'DEV';
  const queued = pending
    .map(queueRow)
    .filter((row) => row.reqTrans?.transActu?.signa?.actu)
    .sort((a, b) => Number(a.createdAt || 0) - Number(b.createdAt || 0));
  const currentActu = current?.order?.reqTrans?.transActu?.signa?.actu ? current.order.reqTrans.transActu : null;
  if (!currentActu && !queued.length) throw new Error('No signed bills to send');
  const limitBytes = readOfflineBatchLimitBytes(storeId);

  const postGroup = async (live: any, rows: any[], prepared?: { reqTrans: any; headers: any }) => {
    const built = offlineBatchRequest(live, rows);
    const transLot = built.transLot;
    const reqTrans = prepared?.reqTrans || built.reqTrans;
    const mode = live ? 'withBill' : 'lot';
    const signatureBills = built.signatureBills;
    if (live) {
      logExceptionScenarios(current?.event || '', 'flush', live, current?.order);
      logExceptionScenarios(current?.event || '', 'send', live, current?.order);
    }
    rows.forEach((row) => {
      logExceptionScenarios(row?.event || '', 'flush', row.reqTrans.transActu, row?.order);
      logExceptionScenarios(row?.event || '', 'send', row.reqTrans.transActu, row?.order);
    });
    const billWithSize = (bill: any) => (
      bill?.noTrans ? `${bill.noTrans} ${offlineBillSizeLabel(bill)}` : null
    );
    console.log('Back to station', 'batch send', {
      mode,
      transActu: billWithSize(live),
      transLot: transLot.map(billWithSize),
      signatureCount: signatureBills.length,
    });
    console.log('[MEV Offline Batch] Batch request', {
      transActu: billWithSize(live),
      newestFirst: transLot.map(billWithSize),
    });
    const headers = prepared?.headers || await transmissionHeaders(
      live || transLot[0],
      storeId,
      envirn,
      signatureBills.map((bill) => bill?.signa?.actu),
    );
    const result = await postPreparedCloudMevSale({
      storeId,
      envirn,
      reqTrans,
      headers,
    });
    if (current?.event === 'kitchen' || rows.some((row) => row.event === 'kitchen')) {
      logKitchenMev(headers, reqTrans, result);
    }
    const retour = result?.rqResponse?.retourTrans || {};
    const failures = failedTransactionNumbers(result);
    const lotConfirmed = transLot.length === 0 || Boolean(retour?.retourTransLot?.noLot);
    const accepted = Boolean(result?.ok) && failures.numbers.size === 0 && !failures.unnamed && lotConfirmed;
    const summary = {
      ...result,
      accepted,
      mode,
      lotCount: transLot.length,
      noLot: retour?.retourTransLot?.noLot || '',
      psiDatLot: retour?.retourTransLot?.psiDatLot || '',
      psiNoTrans: retour?.retourTransActu?.psiNoTrans || '',
      rqError: rqErrorText(result),
    };
    console.log('Back to station', 'batch response', {
      mode,
      accepted,
      ok: result?.ok,
      rqStatus: result?.rqStatus,
      noLot: summary.noLot || null,
      psiDatLot: summary.psiDatLot || null,
      psiNoTrans: summary.psiNoTrans || null,
      lotCount: transLot.length,
      rqError: summary.rqError || null,
    });
    console.log('[MEV Offline Batch] RQ response', result);
    if (!accepted && live?.modImpr !== 'RPR') logRejectedResponse(result, reqTrans);
    return summary;
  };

  const clearAccepted = async (rows: any[]) => {
    for (const row of rows) {
      if (row.queueId == null) continue;
      await mevOfflineQueue.markTransactionSent(row.queueId);
    }
    const remaining = await mevOfflineQueue.getUnsentTransactions(current?.storeId);
    if (!remaining.length) mevOfflineQueue.exitOfflineMode();
  };

  const full = offlineBatchRequest(currentActu, queued);
  const fullHeaders = await transmissionHeaders(
    currentActu || full.transLot[0],
    storeId,
    envirn,
    full.signatureBills.map((bill) => bill?.signa?.actu),
  );
  const fullBytes = offlineRequestBytes(fullHeaders, full.reqTrans);
  const singleBill = queued.length <= (currentActu ? 0 : 1);
  if (fullBytes <= limitBytes || singleBill) {
    const summary = await postGroup(currentActu, queued, { reqTrans: full.reqTrans, headers: fullHeaders });
    if (summary.accepted) await clearAccepted(pending.map((row) => ({ queueId: row.id })));
    return summary;
  }

  console.log('[MEV Offline Batch] Batch size limit exceeded', {
    limitBytes,
    bytes: fullBytes,
    queuedCount: queued.length,
  });
  const headerAllowance = fullBytes - new TextEncoder().encode(JSON.stringify(full.reqTrans)).length;
  let pendingRows = queued;
  let live: any = currentActu;
  let firstSummary: any = null;
  let sentLotCount = 0;

  while (live || pendingRows.length) {
    let rows = oldestRowsWithinLimit(pendingRows, live, limitBytes, headerAllowance);
    let built = offlineBatchRequest(live, rows);
    let headers = await transmissionHeaders(
      live || built.transLot[0],
      storeId,
      envirn,
      built.signatureBills.map((bill) => bill?.signa?.actu),
    );
    while (rows.length > 0 && offlineRequestBytes(headers, built.reqTrans) > limitBytes) {
      if (!live && rows.length === 1) break;
      rows = rows.slice(0, -1);
      if (!rows.length && !live) break;
      built = offlineBatchRequest(live, rows);
      headers = await transmissionHeaders(
        live || built.transLot[0],
        storeId,
        envirn,
        built.signatureBills.map((bill) => bill?.signa?.actu),
      );
    }
    if (!live && !rows.length) {
      if (!firstSummary) throw new Error('No signed bills to send');
      return { ...firstSummary, accepted: false, lotCount: sentLotCount };
    }
    const summary = await postGroup(live, rows, { reqTrans: built.reqTrans, headers });
    if (!summary.accepted) {
      if (!firstSummary) return summary;
      return {
        ...firstSummary,
        accepted: false,
        rqError: summary.rqError || firstSummary.rqError,
        lotCount: sentLotCount,
      };
    }
    await clearAccepted(rows);
    sentLotCount += rows.length;
    pendingRows = pendingRows.slice(rows.length);
    if (!firstSummary) firstSummary = summary;
    live = null;
  }

  if (!firstSummary || pendingRows.length) {
    if (!firstSummary) throw new Error('No signed bills to send');
    return { ...firstSummary, accepted: false, lotCount: sentLotCount };
  }
  await clearAccepted(pending.map((row) => ({ queueId: row.id })));
  return { ...firstSummary, accepted: true, lotCount: sentLotCount };
}

function logoutBatchText(key: string, fallback: string) {
  const language = (typeof localStorage !== 'undefined' && localStorage.getItem('language')) || 'en';
  const packs = localization as Record<string, Record<string, string>>;
  const pack = packs[language] || packs.en;
  return pack?.[key] || packs.en?.[key] || fallback;
}

function showLogoutBatchNotice(result: any, error?: unknown) {
  const failed = !result?.accepted || !result?.noLot;
  if (error || failed) {
    const reason = error instanceof Error ? error.message : (typeof error === 'string' ? error : '');
    toast.warning(logoutBatchText('offlineBillsNotConfirmed', 'Revenue Québec did not confirm the offline bills'), {
      description: result?.rqError || reason || logoutBatchText('unsentBillsStillQueued', 'The offline bills stay on this station until Revenue Québec confirms them.'),
      duration: 9000,
    });
    return;
  }
  const count = Number(result?.lotCount || 0);
  if (!count) return;
  const details = logoutBatchText('offlineBillsReceivedDetails', 'Revenue Québec received {count} offline bill(s). Lot {noLot}. Received {psiDatLot}.')
    .replace('{count}', String(count))
    .replace('{noLot}', result.noLot)
    .replace('{psiDatLot}', result.psiDatLot || '—');
  toast.success(logoutBatchText('offlineBillsSentSuccessfully', 'Offline bills sent successfully'), {
    description: details,
    duration: 10000,
  });
}

let logoutFlush: Promise<any> | null = null;

/** Sends the pending offline lot when a session ends and the network is back. */
export function flushOfflineBillsOnLogout() {
  if (logoutFlush) return logoutFlush;
  logoutFlush = (async () => {
    try {
      if (!connectionMonitor.getStatus()) return null;
      const pending = await mevOfflineQueue.getUnsentTransactions();
      if (!pending.length) return null;
      const summary = await flushOfflineBills();
      if (summary) showLogoutBatchNotice(summary);
      return summary;
    } catch (error) {
      console.error('[MEV Offline Batch] Logout send failed', error);
      showLogoutBatchNotice(null, error);
      return null;
    }
  })().finally(() => {
    logoutFlush = null;
  });
  return logoutFlush;
}

async function printPostedBill(args: {
  storeId: string;
  orderId: string;
  event: string;
  reqTrans: any;
  result: any;
  order?: any;
}) {
  const signedActu = args.reqTrans?.transActu;
  if (signedActu && args.result?.emprCertifSEV) signedActu.emprCertifSEV = args.result.emprCertifSEV;
  const retour = args.result?.rqResponse?.retourTrans?.retourTransActu || {};
  const psiNoTrans = retour.psiNoTrans || '';
  const psiDatTrans = retour.psiDatTrans || '';
  console.log('[MEV timing] print start', {
    orderId: args.orderId,
    noTrans: signedActu?.noTrans || '',
    psiNoTrans: psiNoTrans || '(missing)',
    psiDatTrans: psiDatTrans || '(missing)',
    hasLocalQr: Boolean(args.reqTrans?.qrCodeUrl),
  });
  const printStamp = signedActu?.modImpr === 'RPR'
    ? (signedActu?.signa?.actu || Date.now())
    : (psiNoTrans || signedActu?.signa?.actu || Date.now());
  const eventId = `station-${args.orderId}-${signedActu?.modImpr || 'FAC'}-${printStamp}`;
  console.log('Back to station', 'print posted bill', {
    orderId: args.orderId,
    event: args.event,
    ok: args.result?.ok,
    rqStatus: args.result?.rqStatus,
    noTrans: signedActu?.noTrans || null,
    modImpr: signedActu?.modImpr || null,
    formImpr: signedActu?.formImpr || null,
    psiNoTrans: psiNoTrans || null,
    hasQr: Boolean(args.reqTrans?.qrCodeUrl),
  });
  let qrCodeUrl = args.reqTrans?.qrCodeUrl || '';
  if (!qrCodeUrl && signedActu) {
    try {
      qrCodeUrl = await mevOrderJobManager.generateQRCodeUrl({
        ...signedActu,
        EMPRCERTIFTRANSM: signedActu.emprCertifSEV,
      }, localStorage.getItem('envirn') || 'DEV');
    } catch (error) {
      console.error('[MEV] station QR failed', error);
    }
  }
  const source = args.order || {};
  const receiptEmail = String(source.receiptEmail || source.customerEmail || source.customer?.email || '').trim();
  await mevOrderJobManager.handleCloudMevResponse({
    ...source,
    id: args.orderId,
    ...(receiptEmail ? { receiptEmail, customerEmail: source.customerEmail || receiptEmail } : {}),
    mevEvent: args.event,
    mevEventId: eventId,
    isCorrected: args.event === 'correct_payment_step2' || Boolean(source.isCorrected),
    psiNoTrans,
    cloudMev: {
      event: args.event,
      eventId,
      rqRequest: { reqTrans: { transActu: signedActu } },
      rqResponse: args.result?.rqResponse,
      qrCodeUrl,
    },
  }, args.orderId);
}

export async function postOnlineBill(args: {
  storeId: string;
  orderId: string;
  event: string;
  reqTrans: any;
  order?: any;
}) {
  const envirn = readStoredCloudMevEnv(args.storeId) || 'DEV';
  const sendStarted = Date.now();
  const localQr = args.reqTrans?.qrCodeUrl || '';
  if (args.reqTrans) delete args.reqTrans.qrCodeUrl;
  const reqTrans = signedReqTrans({ reqTrans: args.reqTrans });
  if (!reqTrans) throw new Error('The station bill is not signed');
  const actu = reqTrans.transActu;
  const isAuc = actu?.modPai === 'AUC' && actu?.modImpr === 'PSP';
  if (isAuc) {
    const first = JSON.parse(JSON.stringify(reqTrans));
    first.transActu.formImpr = 'NON';
    delete first.transActu.refs;
    const firstResult = await postPreparedCloudMevSale({
      storeId: args.storeId,
      envirn,
      reqTrans: first,
      headers: await transmissionHeaders(first.transActu, args.storeId, envirn),
    });
    console.log('[MEV] AUC NON passthrough', { noTrans: actu.noTrans, rqStatus: firstResult?.rqStatus });
    if (!firstResult?.ok) return firstResult;
    reqTrans.transActu.formImpr = 'PAP';
  }
  logExceptionScenarios(args.event, 'send', reqTrans.transActu, args.order);
  const headers = await transmissionHeaders(reqTrans.transActu, args.storeId, envirn);
  let result: any;
  try {
    result = await postPreparedCloudMevSale({
      storeId: args.storeId,
      envirn,
      reqTrans,
      headers,
    });
  } catch (error) {
    if (args.event === 'kitchen') {
      logKitchenMev(headers, reqTrans, {
        ok: false,
        error: error instanceof Error ? error.message : String(error),
      });
    }
    restoreChain(reqTrans);
    if (!isReproductionBill(reqTrans, args.order)) throw error;
    console.log('Back to station', 'reproduction prints on this station', {
      orderId: args.orderId,
      noTrans: reqTrans.transActu?.noTrans || null,
      rqError: error instanceof Error ? error.message : String(error),
    });
    if (args.order?.skipStationPrint) {
      return { ok: false, printed: false, rqError: error instanceof Error ? error.message : String(error) };
    }
    if (localQr) reqTrans.qrCodeUrl = localQr;
    await printPostedBill({
      ...args,
      reqTrans,
      result: reproductionPrintResult(args.order, { ok: false }),
    });
    return { ok: false, printed: true, rqError: error instanceof Error ? error.message : String(error) };
  }
  const retour = result?.rqResponse?.retourTrans?.retourTransActu || {};
  console.log('[MEV timing] cloud', {
    noTrans: reqTrans.transActu?.noTrans || '',
    ms: Date.now() - sendStarted,
    ok: result?.ok,
    rqStatus: result?.rqStatus,
    psiNoTrans: retour.psiNoTrans || '',
    psiDatTrans: retour.psiDatTrans || '',
    functionMs: result?.timings || null,
  });
  console.log('[MEV] passthrough response', { noTrans: reqTrans.transActu?.noTrans, rqStatus: result?.rqStatus });
  if (args.event === 'kitchen') {
    logKitchenMev(headers, reqTrans, result);
    if (!result?.ok && !retour.psiNoTrans) {
      restoreChain(reqTrans);
      logRejectedResponse(result, reqTrans);
    }
    return { ...result, printed: false };
  }
  const returned = Boolean(retour.psiNoTrans);
  if (!result?.ok && !returned) {
    restoreChain(reqTrans);
    if (isReproductionBill(reqTrans, args.order)) {
      console.log('Back to station', 'reproduction prints on this station', {
        orderId: args.orderId,
        noTrans: reqTrans.transActu?.noTrans || null,
        rqStatus: result?.rqStatus,
        rqError: rqErrorText(result),
      });
      if (args.order?.skipStationPrint) return { ...result, printed: false };
      if (localQr) reqTrans.qrCodeUrl = localQr;
      await printPostedBill({
        ...args,
        reqTrans,
        result: reproductionPrintResult(args.order, result),
      });
      return { ...result, printed: true };
    }
    logRejectedResponse(result, reqTrans);
    console.log('Back to station', 'skip print, response not ok', { orderId: args.orderId, rqStatus: result?.rqStatus, rqError: rqErrorText(result) });
    console.error('[MEV] station bill was rejected. Not signing it again.', result);
    return result;
  }
  if (args.order?.skipStationPrint) return { ...result, printed: false };
  if (localQr) reqTrans.qrCodeUrl = localQr;
  console.log('Back to station', 'handing online bill to the printer', { orderId: args.orderId });
  await printPostedBill({ ...args, reqTrans, result });
  return result;
}

function reproductionPrintResult(order: any, result: any) {
  const returned = result?.rqResponse?.retourTrans?.retourTransActu?.psiNoTrans;
  if (returned) return result;
  return {
    ...(result || {}),
    rqResponse: {
      retourTrans: {
        retourTransActu: {
          psiNoTrans: order?.psiNoTrans || '',
          psiDatTrans: order?.psiDatTrans || '',
        },
      },
    },
  };
}

export async function printCarriedBill(args: {
  storeId: string;
  orderId: string;
  event: string;
  reqTrans: any;
  result: any;
  order?: any;
}) {
  if (!args.result?.ok || !args.reqTrans?.transActu) {
    if (isReproductionBill(args.reqTrans, args.order) && args.reqTrans?.transActu) {
      console.log('Back to station', 'reproduction prints on this station', { orderId: args.orderId });
      await printPostedBill({
        ...args,
        result: reproductionPrintResult(args.order, args.result),
      });
      return;
    }
    console.log('Back to station', 'skip carried bill print', {
      orderId: args.orderId,
      ok: args.result?.ok,
      hasTransActu: Boolean(args.reqTrans?.transActu),
    });
    return;
  }
  console.log('Back to station', 'handing carried bill to the printer', { orderId: args.orderId });
  await printPostedBill(args);
}

export function isMevBillEvent(event: string) {
  return MEV_ACTIONS.has(event);
}

export async function dispatchStationBill(args: {
  storeId: string;
  orderId: string;
  event: string;
  order: any;
}) {
  if (!args.storeId) throw new Error('Missing store id for the bill');
  const finish = (payload: any, returned: any) => {
    logOnlineOrderDebug(args.order, args.event, payload, returned);
    return returned;
  };
  const offline = !connectionMonitor.getStatus();
  const queuedCount = await mevOfflineQueue.getQueueCount();
  if (offline) {
    const formatted = await queueOfflineBill(args);
    const reqTrans = formatted?.reqTrans || null;
    return finish(reqTrans, {
      offline: true,
      ok: Boolean(reqTrans?.transActu?.signa?.actu),
      reqTrans,
    });
  }
  let reqTrans: any = null;
  try {
    reqTrans = await prepareStationBill(args.order, args.event);
    if (!reqTrans?.transActu?.signa?.actu) throw new Error('The station could not sign the bill');
    if (queuedCount > 0) {
      const posted = await flushOfflineBills({
        orderId: args.orderId,
        event: args.event,
        order: { ...args.order, reqTrans },
        storeId: args.storeId,
      });
      if (args.event !== 'kitchen' && (posted?.ok || reqTrans?.transActu?.modImpr === 'RPR')) {
        await printCarriedBill({
          storeId: args.storeId,
          orderId: args.orderId,
          event: args.event,
          reqTrans,
          result: posted,
          order: args.order,
        });
      }
      return finish(reqTrans, { offline: false, reqTrans, ...(posted || {}) });
    }
    const result = await postOnlineBill({
      storeId: args.storeId,
      orderId: args.orderId,
      event: args.event,
      reqTrans,
      order: args.order,
    });
    return finish(reqTrans, { offline: false, reqTrans, ...(result || {}) });
  } catch (error) {
    finish(reqTrans, {
      ok: false,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
}
